import { paginateNdtGlobalItems, type NdtGlobalListQuery, type NdtGlobalListResult } from "../../domain/ndt/ndt-global-list-query.js";
import type { RequestContext } from "../context/request-context.js";
import type { NdtProcedureRepository } from "../../repositories/operational-repositories.js";
import {
  type NdtMethod,
  type NdtProcedure,
  type NdtProcedureFilters,
  type NdtProcedureStatus,
  applyProcedureFilters,
  selectableProceduresForReport,
  validateNdtProcedure
} from "../../domain/ndt/ndt-procedure.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/** Answers whether a procedure is referenced by any report, so a linked one is never physically deleted. */
export type NdtProcedureUsageChecker = (procedureId: string) => Promise<boolean>;

export class NdtProcedureService {
  constructor(
    private readonly repo: NdtProcedureRepository,
    private readonly isProcedureInUse?: NdtProcedureUsageChecker
  ) {}

  /** M21: limite 100 no backend, busca global sem escopo de Cliente. */
  async listPaged(context: RequestContext, input: NdtGlobalListQuery): Promise<NdtGlobalListResult<NdtProcedure>> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryRead);
    const repository = this.repo as unknown as {
      listPaged?: (query: NdtGlobalListQuery) => Promise<NdtGlobalListResult<NdtProcedure>>;
    };
    if (repository.listPaged) return repository.listPaged(input);
    return paginateNdtGlobalItems(await this.repo.listAll(), input);
  }

  async list(context: RequestContext, filters?: NdtProcedureFilters): Promise<NdtProcedure[]> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryRead);
    const all = await this.repo.listAll();
    const ordered = [...all].sort((a, b) => a.method.localeCompare(b.method) || a.code.localeCompare(b.code));
    return filters ? applyProcedureFilters(ordered, filters) : ordered;
  }

  async getById(context: RequestContext, id: string): Promise<NdtProcedure | null> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryRead);
    return this.repo.findById(id);
  }

  /**
   * Procedures a report of `method` may offer: ACTIVE ones only, plus the one
   * the report is already linked to — an inactivated procedure stays visible
   * on the reports that already used it, but never appears for a new one.
   */
  async listSelectableForReport(
    context: RequestContext,
    method: NdtMethod,
    currentlyLinkedProcedureId?: string | null
  ): Promise<NdtProcedure[]> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryRead);
    const all = await this.repo.listAll();
    return selectableProceduresForReport(all, method, currentlyLinkedProcedureId)
      .sort((a, b) => a.code.localeCompare(b.code));
  }

  async create(context: RequestContext, data: Partial<NdtProcedure>): Promise<NdtProcedure> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryCreate);

    const errors = validateNdtProcedure(data);
    if (errors.length > 0) {
      throw new AppError(ErrorCode.Validation, `Invalid NDT procedure: ${errors.join(" ")}`, {
        safeMessage: errors.join(" ")
      });
    }

    await this.assertCodeIsUnique(data.method as NdtMethod, data.code!.trim(), null);

    const now = new Date().toISOString();
    const procedure: NdtProcedure = {
      id: `PROC-${randomUUID()}`,
      method: data.method as NdtMethod,
      code: data.code!.trim(),
      title: data.title!.trim(),
      revision: data.revision || "",
      issueDate: data.issueDate || "",
      validityDate: data.validityDate || "",
      referenceStandard: data.referenceStandard || "",
      acceptanceCriteria: data.acceptanceCriteria || "",
      defaultTechnique: data.defaultTechnique || "",
      description: data.description || "",
      status: (data.status as NdtProcedureStatus) || "ACTIVE",
      observations: data.observations || "",
      attachments: data.attachments || [],
      hotCorrectionReferenceVelocityMs: data.hotCorrectionReferenceVelocityMs ?? null,
      hotCorrectionCoefficientK: data.hotCorrectionCoefficientK ?? null,
      hotCorrectionReferenceTemperatureC: data.hotCorrectionReferenceTemperatureC ?? null,
      createdAt: now,
      updatedAt: now,
      createdBy: context.userId || "system",
      updatedBy: context.userId || "system"
    };

    await this.repo.save(procedure);
    return procedure;
  }

  async update(context: RequestContext, id: string, data: Partial<NdtProcedure>): Promise<NdtProcedure> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryUpdate);

    const existing = await this.requireProcedure(id);
    const merged: NdtProcedure = { ...existing, ...data, id: existing.id, createdAt: existing.createdAt, createdBy: existing.createdBy };

    const errors = validateNdtProcedure(merged);
    if (errors.length > 0) {
      throw new AppError(ErrorCode.Validation, `Invalid NDT procedure: ${errors.join(" ")}`, {
        safeMessage: errors.join(" ")
      });
    }

    await this.assertCodeIsUnique(merged.method, merged.code.trim(), existing.id);

    const updated: NdtProcedure = {
      ...merged,
      code: merged.code.trim(),
      title: merged.title.trim(),
      updatedAt: new Date().toISOString(),
      updatedBy: context.userId || "system"
    };

    await this.repo.save(updated);
    return updated;
  }

  async setStatus(context: RequestContext, id: string, status: NdtProcedureStatus): Promise<NdtProcedure> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryInactivate);

    const existing = await this.requireProcedure(id);
    const updated: NdtProcedure = {
      ...existing,
      status,
      updatedAt: new Date().toISOString(),
      updatedBy: context.userId || "system"
    };
    await this.repo.save(updated);
    return updated;
  }

  /**
   * Physical deletion is only ever allowed for a procedure no report
   * references. A linked one is refused with an instruction to inactivate,
   * mirroring the Equipment/Instrument deletion rule already in the system.
   */
  async delete(context: RequestContext, id: string): Promise<void> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryDelete);

    const existing = await this.requireProcedure(id);

    if (this.isProcedureInUse && await this.isProcedureInUse(existing.id)) {
      throw new AppError(ErrorCode.Conflict, "NDT procedure is referenced by a report", {
        safeMessage: `O procedimento ${existing.code} já está vinculado a um relatório e não pode ser excluído. Utilize a ação de inativar.`
      });
    }

    await this.repo.delete(existing.id);
  }

  private async requireProcedure(id: string): Promise<NdtProcedure> {
    const existing = await this.repo.findById(id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "NDT procedure not found", {
        safeMessage: "Procedimento de END não encontrado."
      });
    }
    return existing;
  }

  private async assertCodeIsUnique(method: NdtMethod, code: string, ignoreId: string | null): Promise<void> {
    const all = await this.repo.listAll();
    const duplicate = all.find(p =>
      p.id !== ignoreId &&
      p.method === method &&
      p.code.trim().toLowerCase() === code.toLowerCase()
    );
    if (duplicate) {
      throw new AppError(ErrorCode.Conflict, "Duplicated NDT procedure code", {
        safeMessage: `Já existe um procedimento ${method} com o código ${code}.`
      });
    }
  }
}
